import { useMsal } from '@azure/msal-react';
import { Container, Grid } from '@mui/material';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ToastSeverity, theme, useToast } from 'thermia-react-container';
import { RegistrationsContext } from '../../context/contextProvider';
import useIsMounted from '../../hooks/useIsMounted';
import { ApiErrorHelper } from '../../validation/Errors';
import Wrapper from './containers/wrapper';

interface Props {}

const UnregisteredDevices: FC<Props> = () => {
  const { devices } = useContext(RegistrationsContext);
  const { devices: deviceData, fetchDevices, isDevicesLoading } = devices;
  const [loading, setLoading] = useState<boolean>(true);
  const { t } = useTranslation('registrations');
  const { addToast } = useToast();
  const isMounted = useIsMounted();
  const { instance } = useMsal();

  const handleFetchUnregisteredDevices = useCallback(async () => {
    setLoading(true);
    try {
      await fetchDevices();
    } catch (e) {
      if (isMounted) {
        if (ApiErrorHelper.isOnsiteVersionInvalid(e)) {
          addToast(t('registrations.general.errorOnsiteVersionIsInvalid'), ToastSeverity.ERROR);
        } else {
          addToast(t('registrations.general.errorFetchDevices'), ToastSeverity.ERROR);
        }
      }
    }
    if (isMounted) {
      setLoading(false);
    }
  }, [addToast, fetchDevices, instance, isMounted, t]);

  useEffect(() => {
    if (!deviceData && !isDevicesLoading) {
      handleFetchUnregisteredDevices();
    }
  }, [loading, handleFetchUnregisteredDevices, deviceData, isDevicesLoading]);

  return (
    <Container maxWidth="xl" sx={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(50) }}>
      {devices && <Wrapper isLoading={isDevicesLoading} />}
      {!deviceData && !isDevicesLoading && !loading && (
        <Grid container justifyContent={'center'}>
          <Button
            variant="contained"
            style={{ backgroundColor: theme.palette.error.main }}
            title={`${t('registrations.general.errorFetchDevices')} - ${t('registrations.general.retry')}`}
            loading={isDevicesLoading}
            onClick={handleFetchUnregisteredDevices}
          />
        </Grid>
      )}
    </Container>
  );
};

export default UnregisteredDevices;
